/**
 * ExtractionPreview — Sprint 23 F006
 *
 * Displays extracted financial data from a PDF extraction job for user review
 * before committing to the financial period.
 *
 * Features:
 *   - Displays P&L and BS items with confidence indicators
 *   - Allows manual mapping overrides via Select dropdowns
 *   - Checkbox selection to choose which items to import
 *   - "Select All / Deselect All" toggle
 *   - Import count updates in real-time
 *   - Calls POST /api/financial/extract/:jobId/confirm on confirmation
 */

import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ConfidenceIndicator } from "./confidence-indicator";

// ============================================================================
// Types
// ============================================================================

type PLLineItemType =
  | 'income_sales' | 'other_income' | 'cost_of_goods_sold' | 'wages_salaries'
  | 'rent_premises' | 'utilities' | 'insurance' | 'marketing' | 'professional_fees'
  | 'motor_vehicle_expenses' | 'office_expenses' | 'other_operating_expenses'
  | 'depreciation' | 'amortisation' | 'interest_expense' | 'lease_expense'
  | 'tax_expense' | 'rental_income_smsf' | 'investment_income_smsf'
  | 'member_contributions' | 'fund_expenses';

type BSLineItemType =
  | 'cash_business_account' | 'accounts_receivable' | 'inventory' | 'prepayments'
  | 'other_current_assets' | 'property_plant_equipment' | 'investments'
  | 'intangibles' | 'goodwill' | 'other_non_current_assets' | 'accounts_payable'
  | 'short_term_debt' | 'tax_payable' | 'provisions' | 'other_current_liabilities'
  | 'long_term_debt' | 'deferred_tax_liabilities' | 'other_non_current_liabilities'
  | 'share_capital' | 'retained_earnings' | 'reserves' | 'other_equity'
  | 'cash_holdings_smsf' | 'property_values_smsf' | 'other_assets_smsf'
  | 'loan_balance_smsf';

type LineItemType = PLLineItemType | BSLineItemType;

export interface MappedLineItem {
  lineItemType: LineItemType;
  label: string;
  sourceLabel: string;
  amount: number;
  confidence: number;
  matchType: 'exact' | 'fuzzy' | 'suggested';
  /** Period-keyed values (from multi-year extraction) */
  values?: Record<string, number>;
}

export interface UnmappedItem {
  sourceLabel: string;
  values: Record<string, number>;
  suggestedType?: LineItemType;
  suggestedConfidence?: number;
}

export interface ExtractionMetadata {
  periodsDetected: string[];
  overallConfidence: number;
  totalLineItems: number;
  mappedCount: number;
  unmappedCount: number;
  provider?: string;
  sourceFileName?: string;
}

export interface ExtractedData {
  profitLoss: MappedLineItem[];
  balanceSheet: MappedLineItem[];
  unmapped: UnmappedItem[];
  metadata: ExtractionMetadata;
}

export interface ExtractionPreviewProps {
  jobId: string;
  financialPeriodId: string;
  extractedData: ExtractedData;
  onComplete?: (result: { profitLoss: number; balanceSheet: number }) => void;
  onCancel?: () => void;
}

// ============================================================================
// Line item label maps
// ============================================================================

const PL_LABELS: Record<PLLineItemType, string> = {
  income_sales: 'Revenue / Sales',
  other_income: 'Other Income',
  cost_of_goods_sold: 'Cost of Goods Sold',
  wages_salaries: 'Wages and Salaries',
  rent_premises: 'Rent',
  utilities: 'Utilities',
  insurance: 'Insurance',
  marketing: 'Marketing',
  professional_fees: 'Professional Fees',
  motor_vehicle_expenses: 'Motor Vehicle',
  office_expenses: 'Office Expenses',
  other_operating_expenses: 'Other Operating Expenses',
  depreciation: 'Depreciation',
  amortisation: 'Amortisation',
  interest_expense: 'Interest Expense',
  lease_expense: 'Lease Expense',
  tax_expense: 'Tax Expense',
  rental_income_smsf: 'Rental Income (SMSF)',
  investment_income_smsf: 'Investment Income (SMSF)',
  member_contributions: 'Member Contributions',
  fund_expenses: 'Fund Expenses',
};

const BS_LABELS: Record<BSLineItemType, string> = {
  cash_business_account: 'Cash and Cash Equivalents',
  accounts_receivable: 'Accounts Receivable',
  inventory: 'Inventory',
  prepayments: 'Prepayments',
  other_current_assets: 'Other Current Assets',
  property_plant_equipment: 'Property, Plant and Equipment',
  investments: 'Investments',
  intangibles: 'Intangibles',
  goodwill: 'Goodwill',
  other_non_current_assets: 'Other Non-Current Assets',
  accounts_payable: 'Accounts Payable',
  short_term_debt: 'Short Term Debt',
  tax_payable: 'Tax Payable',
  provisions: 'Provisions',
  other_current_liabilities: 'Other Current Liabilities',
  long_term_debt: 'Long Term Debt',
  deferred_tax_liabilities: 'Deferred Tax Liabilities',
  other_non_current_liabilities: 'Other Non-Current Liabilities',
  share_capital: 'Share Capital',
  retained_earnings: 'Retained Earnings',
  reserves: 'Reserves',
  other_equity: 'Other Equity',
  cash_holdings_smsf: 'Cash Holdings (SMSF)',
  property_values_smsf: 'Property Values (SMSF)',
  other_assets_smsf: 'Other Assets (SMSF)',
  loan_balance_smsf: 'Loan Balance (SMSF)',
};

const ALL_PL_TYPES = Object.keys(PL_LABELS) as PLLineItemType[];
const ALL_BS_TYPES = Object.keys(BS_LABELS) as BSLineItemType[];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getItemKey(item: MappedLineItem, idx: number): string {
  return `${item.lineItemType}-${idx}`;
}

// ============================================================================
// Component
// ============================================================================

export function ExtractionPreview({
  jobId,
  financialPeriodId,
  extractedData,
  onComplete,
  onCancel,
}: ExtractionPreviewProps) {
  const { toast } = useToast();
  const { profitLoss, balanceSheet, unmapped, metadata } = extractedData;

  // ---- State: checked items (keys are "{lineItemType}-{idx}" or "unmapped-{idx}") ----
  const initialChecked = useMemo(() => {
    const set = new Set<string>();
    profitLoss.forEach((item, idx) => {
      if (item.confidence > 0.60) set.add(getItemKey(item, idx));
    });
    balanceSheet.forEach((item, idx) => {
      if (item.confidence > 0.60) set.add(`bs-${item.lineItemType}-${idx}`);
    });
    // Unmapped items are unchecked by default
    return set;
  }, [profitLoss, balanceSheet]);

  const [checked, setChecked] = useState<Set<string>>(initialChecked);

  // ---- State: mapping overrides ----
  const [plMappings, setPlMappings] = useState<Record<number, PLLineItemType>>({});
  const [bsMappings, setBsMappings] = useState<Record<number, BSLineItemType>>({});
  const [unmappedMappings, setUnmappedMappings] = useState<Record<number, LineItemType>>({});
  const [skippedUnmapped, setSkippedUnmapped] = useState<Set<number>>(new Set());

  // ---- State: import result ----
  const [importResult, setImportResult] = useState<{ profitLoss: number; balanceSheet: number } | null>(null);

  // ---- Derived: import counts ----
  const selectedPlCount = profitLoss.filter((item, idx) => checked.has(getItemKey(item, idx))).length;
  const selectedBsCount = balanceSheet.filter((item, idx) => checked.has(`bs-${item.lineItemType}-${idx}`)).length;
  const selectedUnmappedCount = unmapped.filter(
    (_, idx) => !skippedUnmapped.has(idx) && checked.has(`unmapped-${idx}`)
  ).length;
  const totalSelected = selectedPlCount + selectedBsCount + selectedUnmappedCount;
  const totalItems = profitLoss.length + balanceSheet.length + unmapped.filter((_, idx) => !skippedUnmapped.has(idx)).length;

  // ---- Toggle helpers ----
  function toggleItem(key: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSelectAll() {
    if (totalSelected === totalItems) {
      // Deselect all
      setChecked(new Set());
    } else {
      // Select all
      const allKeys = new Set<string>();
      profitLoss.forEach((item, idx) => allKeys.add(getItemKey(item, idx)));
      balanceSheet.forEach((item, idx) => allKeys.add(`bs-${item.lineItemType}-${idx}`));
      unmapped.forEach((_, idx) => {
        if (!skippedUnmapped.has(idx)) allKeys.add(`unmapped-${idx}`);
      });
      setChecked(allKeys);
    }
  }

  // ---- Confirm mutation ----
  const confirmMutation = useMutation({
    mutationFn: async () => {
      const plItems = profitLoss
        .filter((item, idx) => checked.has(getItemKey(item, idx)))
        .map((item, idx) => ({
          lineItemType: plMappings[idx] ?? item.lineItemType,
          amount: item.amount,
        }));

      const bsItems = balanceSheet
        .filter((item, idx) => checked.has(`bs-${item.lineItemType}-${idx}`))
        .map((item, idx) => ({
          lineItemType: bsMappings[idx] ?? item.lineItemType,
          amount: item.amount,
        }));

      // Unmapped items that have been assigned a type and checked
      const mappedUnmappedPl: Array<{ lineItemType: string; amount: number }> = [];
      const mappedUnmappedBs: Array<{ lineItemType: string; amount: number }> = [];

      unmapped.forEach((item, idx) => {
        if (skippedUnmapped.has(idx)) return;
        if (!checked.has(`unmapped-${idx}`)) return;
        const mappedType = unmappedMappings[idx] ?? item.suggestedType;
        if (!mappedType) return;

        const amount = Object.values(item.values)[0] ?? 0;

        if (ALL_PL_TYPES.includes(mappedType as PLLineItemType)) {
          mappedUnmappedPl.push({ lineItemType: mappedType, amount });
        } else if (ALL_BS_TYPES.includes(mappedType as BSLineItemType)) {
          mappedUnmappedBs.push({ lineItemType: mappedType, amount });
        }
      });

      const payload = {
        financialPeriodId,
        mappedData: {
          profitLoss: [...plItems, ...mappedUnmappedPl],
          balanceSheet: [...bsItems, ...mappedUnmappedBs],
        },
      };

      const res = await apiRequest('POST', `/api/financial/extract/${jobId}/confirm`, payload);
      return res.json() as Promise<{ jobId: string; status: string; created: { profitLoss: number; balanceSheet: number } }>;
    },
    onSuccess: (data) => {
      const result = data.created;
      setImportResult(result);
      toast({
        title: 'Financial data imported from PDF',
        description: `Imported ${result.profitLoss} P&L and ${result.balanceSheet} Balance Sheet items.`,
      });
      onComplete?.(result);
    },
    onError: (error: Error) => {
      toast({
        title: 'Import failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ---- Success state ----
  if (importResult) {
    return (
      <div data-testid="panel-extraction-preview" className="space-y-4">
        <div className="flex flex-col items-center justify-center gap-3 py-10">
          <CheckCircle2 className="h-12 w-12 text-green-500" />
          <h3 className="text-lg font-semibold">Import Complete</h3>
          <p className="text-muted-foreground text-sm text-center">
            Successfully imported{' '}
            <strong>{importResult.profitLoss}</strong> P&amp;L and{' '}
            <strong>{importResult.balanceSheet}</strong> Balance Sheet items.
          </p>
          {metadata.sourceFileName && (
            <p className="text-xs text-muted-foreground">
              Source: Extracted from {metadata.sourceFileName}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="panel-extraction-preview" className="space-y-4">
      {/* Header */}
      <div
        data-testid="extraction-header"
        className="flex flex-col gap-1 pb-3 border-b"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Source:</span>
            <span className="text-sm text-muted-foreground">
              {metadata.sourceFileName ?? 'Unknown file'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Confidence:</span>
            <Badge variant="outline" className="text-xs">
              {Math.round(metadata.overallConfidence * 100)}%
            </Badge>
            {metadata.provider && (
              <span className="text-xs text-muted-foreground">via {metadata.provider}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{metadata.mappedCount} mapped</span>
          {metadata.unmappedCount > 0 && (
            <span className="text-amber-600">{metadata.unmappedCount} unmapped</span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <span data-testid="import-count" className="text-sm text-muted-foreground">
          Import <strong>{totalSelected}</strong> of <strong>{totalItems}</strong> items
        </span>
        <Button
          data-testid="button-select-all"
          variant="outline"
          size="sm"
          onClick={handleSelectAll}
        >
          {totalSelected === totalItems ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {/* Profit & Loss Section */}
      {profitLoss.length > 0 && (
        <div data-testid="section-profit-loss">
          <h4 className="text-sm font-semibold mb-2">Profit &amp; Loss</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <span className="sr-only">Include</span>
                </TableHead>
                <TableHead>Line Item Type</TableHead>
                <TableHead>Source Label</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center w-12">Conf</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profitLoss.map((item, idx) => {
                const key = getItemKey(item, idx);
                const isChecked = checked.has(key);
                const currentType = plMappings[idx] ?? item.lineItemType;
                return (
                  <TableRow
                    key={key}
                    data-testid={`row-pl-${item.lineItemType}`}
                    className={!isChecked ? 'opacity-50' : undefined}
                  >
                    <TableCell className="py-2">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleItem(key)}
                        aria-label={`Include ${item.label}`}
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      <Select
                        value={currentType}
                        onValueChange={(val) =>
                          setPlMappings((prev) => ({ ...prev, [idx]: val as PLLineItemType }))
                        }
                      >
                        <SelectTrigger className="h-7 text-xs border-0 shadow-none px-0 w-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_PL_TYPES.map((type) => (
                            <SelectItem key={type} value={type} className="text-xs">
                              {PL_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground">
                      {item.sourceLabel}
                    </TableCell>
                    <TableCell className="py-2 text-right text-xs">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <ConfidenceIndicator confidence={item.confidence} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Balance Sheet Section */}
      {balanceSheet.length > 0 && (
        <div data-testid="section-balance-sheet">
          <h4 className="text-sm font-semibold mb-2">Balance Sheet</h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <span className="sr-only">Include</span>
                </TableHead>
                <TableHead>Line Item Type</TableHead>
                <TableHead>Source Label</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center w-12">Conf</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {balanceSheet.map((item, idx) => {
                const key = `bs-${item.lineItemType}-${idx}`;
                const isChecked = checked.has(key);
                const currentType = bsMappings[idx] ?? item.lineItemType;
                return (
                  <TableRow
                    key={key}
                    data-testid={`row-bs-${item.lineItemType}`}
                    className={!isChecked ? 'opacity-50' : undefined}
                  >
                    <TableCell className="py-2">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleItem(key)}
                        aria-label={`Include ${item.label}`}
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      <Select
                        value={currentType}
                        onValueChange={(val) =>
                          setBsMappings((prev) => ({ ...prev, [idx]: val as BSLineItemType }))
                        }
                      >
                        <SelectTrigger className="h-7 text-xs border-0 shadow-none px-0 w-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_BS_TYPES.map((type) => (
                            <SelectItem key={type} value={type} className="text-xs">
                              {BS_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground">
                      {item.sourceLabel}
                    </TableCell>
                    <TableCell className="py-2 text-right text-xs">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <ConfidenceIndicator confidence={item.confidence} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Unmapped Section */}
      {unmapped.length > 0 && (
        <div data-testid="section-unmapped">
          <h4 className="text-sm font-semibold mb-2 text-amber-700">
            Unmapped Items ({unmapped.filter((_, idx) => !skippedUnmapped.has(idx)).length})
          </h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <span className="sr-only">Include</span>
                </TableHead>
                <TableHead>Source Label</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Map To</TableHead>
                <TableHead className="w-16">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unmapped.map((item, idx) => {
                if (skippedUnmapped.has(idx)) return null;
                const key = `unmapped-${idx}`;
                const isChecked = checked.has(key);
                const currentMapping = unmappedMappings[idx] ?? item.suggestedType;
                const amount = Object.values(item.values)[0] ?? 0;
                return (
                  <TableRow
                    key={key}
                    className={!isChecked ? 'opacity-50' : undefined}
                  >
                    <TableCell className="py-2">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleItem(key)}
                        aria-label={`Include ${item.sourceLabel}`}
                        disabled={!currentMapping}
                      />
                    </TableCell>
                    <TableCell className="py-2 text-xs">
                      {item.sourceLabel}
                      {item.suggestedConfidence !== undefined && (
                        <span className="ml-1 text-muted-foreground">
                          ({Math.round(item.suggestedConfidence * 100)}% match)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right text-xs">
                      {formatCurrency(amount)}
                    </TableCell>
                    <TableCell className="py-2">
                      <Select
                        value={currentMapping ?? 'none'}
                        onValueChange={(val) => {
                          if (val === 'none') {
                            setUnmappedMappings((prev) => {
                              const next = { ...prev };
                              delete next[idx];
                              return next;
                            });
                          } else {
                            setUnmappedMappings((prev) => ({ ...prev, [idx]: val as LineItemType }));
                          }
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs w-full">
                          <SelectValue placeholder="Select mapping..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" className="text-xs text-muted-foreground">
                            — Select mapping —
                          </SelectItem>
                          <SelectItem value="income_sales" className="text-xs">P&amp;L: Revenue / Sales</SelectItem>
                          <SelectItem value="other_income" className="text-xs">P&amp;L: Other Income</SelectItem>
                          <SelectItem value="cost_of_goods_sold" className="text-xs">P&amp;L: COGS</SelectItem>
                          <SelectItem value="wages_salaries" className="text-xs">P&amp;L: Wages and Salaries</SelectItem>
                          <SelectItem value="rent_premises" className="text-xs">P&amp;L: Rent</SelectItem>
                          <SelectItem value="utilities" className="text-xs">P&amp;L: Utilities</SelectItem>
                          <SelectItem value="insurance" className="text-xs">P&amp;L: Insurance</SelectItem>
                          <SelectItem value="marketing" className="text-xs">P&amp;L: Marketing</SelectItem>
                          <SelectItem value="professional_fees" className="text-xs">P&amp;L: Professional Fees</SelectItem>
                          <SelectItem value="motor_vehicle_expenses" className="text-xs">P&amp;L: Motor Vehicle</SelectItem>
                          <SelectItem value="office_expenses" className="text-xs">P&amp;L: Office Expenses</SelectItem>
                          <SelectItem value="other_operating_expenses" className="text-xs">P&amp;L: Other Operating Expenses</SelectItem>
                          <SelectItem value="depreciation" className="text-xs">P&amp;L: Depreciation</SelectItem>
                          <SelectItem value="amortisation" className="text-xs">P&amp;L: Amortisation</SelectItem>
                          <SelectItem value="interest_expense" className="text-xs">P&amp;L: Interest Expense</SelectItem>
                          <SelectItem value="tax_expense" className="text-xs">P&amp;L: Tax Expense</SelectItem>
                          <SelectItem value="cash_business_account" className="text-xs">BS: Cash and Cash Equivalents</SelectItem>
                          <SelectItem value="accounts_receivable" className="text-xs">BS: Accounts Receivable</SelectItem>
                          <SelectItem value="inventory" className="text-xs">BS: Inventory</SelectItem>
                          <SelectItem value="accounts_payable" className="text-xs">BS: Accounts Payable</SelectItem>
                          <SelectItem value="long_term_debt" className="text-xs">BS: Long Term Debt</SelectItem>
                          <SelectItem value="short_term_debt" className="text-xs">BS: Short Term Debt</SelectItem>
                          <SelectItem value="retained_earnings" className="text-xs">BS: Retained Earnings</SelectItem>
                          <SelectItem value="share_capital" className="text-xs">BS: Share Capital</SelectItem>
                          <SelectItem value="other_equity" className="text-xs">BS: Other Equity</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-muted-foreground"
                        onClick={() => {
                          setSkippedUnmapped((prev) => {
                            const next = new Set(prev);
                            next.add(idx);
                            return next;
                          });
                          setChecked((prev) => {
                            const next = new Set(prev);
                            next.delete(key);
                            return next;
                          });
                        }}
                      >
                        Skip
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-3 border-t">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={confirmMutation.isPending}>
            Cancel
          </Button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {confirmMutation.isPending && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Importing financial data...
            </span>
          )}
          {confirmMutation.isError && (
            <span className="text-xs text-destructive flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              Import failed
            </span>
          )}
          <Button
            data-testid="button-import-confirm"
            onClick={() => confirmMutation.mutate()}
            disabled={confirmMutation.isPending || totalSelected === 0}
          >
            {confirmMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Importing...
              </>
            ) : (
              `Import Selected Items (${totalSelected})`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
